import { useSelector } from 'react-redux';
import { Input, Paragrapgh } from '../../shared';
import { selectLang } from '../../../store/ui/ui.selector';
import { componentTranslate, translate } from '../../../lib/lang.helper';
import {
  IMusselGrowingCycle,
  IOysterGrowingCycle,
  ISeaweedGrowingCycle,
} from '../../../entities/growing.entities';
import { defaultDateFormat, toMillisecond } from '../../../lib/common.helpers';
import {
  SelectInventoriesByFarmID,
} from '../../../store/extra/extra.selector';
import { selectAccount } from '../../../store/auth/auth.selector';
import { useMemo } from 'react';
import moment from 'moment';
import { IInventorySum } from '../../../entities/inventory.entities';
import { TBusinessType } from '../../../entities/general.entities';
import { inventoryLabel } from '../../../lib/farm.helpers';

interface IAvailable {
  inventory_id: number;
  available: number;
}

interface Props {
  farmID: number;
  type: TBusinessType;
  cycleData: IMusselGrowingCycle | IOysterGrowingCycle | ISeaweedGrowingCycle;
  shellSize?: number | null;
  form?: IInventorySum[];
  onChange: (d: IInventorySum[]) => void;
  pastInventories?: IInventorySum[];
  hideIntro?: boolean;
}

const ReturningInventoriesForm = ({
  farmID,
  type: cropType,
  cycleData,
  shellSize,
  form,
  onChange,
  pastInventories,
  hideIntro,
}: Props) => {
  const lang = useSelector(selectLang);
  const seedStages = useSelector(selectAccount)?.seed_stages_size;
  const allInventories = SelectInventoriesByFarmID(farmID);

  const { plannedDaysDiff, currentDaysDiff } = useMemo(() => {
    const t = moment().format('YYYY-MM-DD');
    const s = moment(
      toMillisecond(cycleData.main_seed.planned_date_seed),
    ).format('YYYY-MM-DD');

    let h: any = null;
    if (!seedStages || shellSize === null) {
      h = moment(
        (cycleData.last_assessment?.planned_date_harvest ??
          cycleData.main_seed.planned_date_harvest) * 1000,
      ).format('YYYY-MM-DD');
    } else {
      let i = 0;
      for (
        i = 0;
        i < seedStages.length && seedStages[i].size <= Number(shellSize);
        i++
      );
      if (i > 0) {
        h = moment(s).add(seedStages[i - 1].time, 'months');
      } else {
        h = moment(
          (cycleData.last_assessment?.planned_date_harvest ??
            cycleData.main_seed.planned_date_harvest) * 1000,
        ).format('YYYY-MM-DD');
      }
    }
    return {
      plannedDaysDiff: moment(t).diff(moment(h), 'd'),
      currentDaysDiff: moment(t).diff(moment(s), 'd'),
    };
  }, [cycleData, seedStages, shellSize]);

  const availableInventories = useMemo(() => {
    let res: IAvailable[] = [];
    if (cycleData.inventories_sum) {
      res = cycleData.inventories_sum.map(item => ({
        inventory_id: item.inventory_id,
        available: item.quantity,
      }));
    }
    if (pastInventories) {
      for (let x of pastInventories) {
        if (res.some(y => y.inventory_id === x.inventory_id)) continue;
        res.push({ inventory_id: x.inventory_id, available: 0 });
      }
    }
    return res;
  }, [cycleData, pastInventories]);

  const updateForm = (ivt_id: number, val: string) => {
    let v = val.length <= 0 ? 0 : Number(val);
    let f = form ? [...form] : [];
    const i = f.findIndex(x => x.inventory_id === ivt_id);
    const inventory = allInventories.find(x => x.id === ivt_id);
    if (!inventory) return;

    const available = availableInventories.find(x => x.inventory_id === ivt_id);
    if (!available) return;

    if (v > available.available) {
      v = available.available;
    }

    if (i < 0) {
      f.push({
        inventory_id: ivt_id,
        quantity: v,
        inventory_name: inventory.name,
        inventory_type: inventory.type,
        seeding_id: cycleData.main_seed.id,
      });
    } else {
      f[i].quantity = v;
    }
    onChange(f);
  };
  const label = (i: number) => {
    const t = allInventories.find(x => x.id === i);
    if (!t) return 'Invalid inventory';

    if (cropType === 'MUSSEL') {
      return inventoryLabel(t);
    } else {
      return translate(
        lang,
        'Number of %s that are being harvested',
        inventoryLabel(t),
      );
    }
  };

  return (
    <>
      {!hideIntro && (
        <Paragrapgh
          size={2}
          color='black'
          align='left'
          fontWeight={400}
          className='pb-25'
        >
          {componentTranslate(
            lang,
            '_harvest_intro',
            <span key={1} className='pr-6 pl-3 font-weight-500'>
              {defaultDateFormat(cycleData.main_seed.planned_date_seed)}
            </span>,
            <span key={2} className='pl-4 font-weight-500'>
              {defaultDateFormat(
                cycleData.last_assessment?.planned_date_harvest ??
                  cycleData.main_seed.planned_date_harvest,
              )}
            </span>,
          )}
          <br />
          <span style={{ fontWeight: 700, color: 'black' }}>
            {translate(
              lang,
              '_harvest_days_schedule',
              Math.abs(plannedDaysDiff),
              plannedDaysDiff <= 0 ? ' ahead' : ' behind',
            )}
            {plannedDaysDiff <= 0 && ' Well done!'}
            <br />
            {translate(lang, '_crop_grew_days', currentDaysDiff)}
          </span>
        </Paragrapgh>
      )}
      {availableInventories.length > 0 && (
        <div className='mb-32 mt-17'>
          <Paragrapgh
            size={2}
            color='black'
            align='left'
            fontWeight={400}
            className='pb-12'
          >
            {translate(
              lang,
              '_input_qty_inventories_returning',
            )}
          </Paragrapgh>
          {availableInventories.map(item => (
            <div className='mb-17' key={item.inventory_id}>
              <Input
                label={label(item.inventory_id)}
                placeholder={translate(
                  lang,
                  'Maximum value is %s',
                  item.available,
                )}
                type='number'
                onChange={e => updateForm(item.inventory_id, e.target.value)}
                value={
                  form
                    ?.find(x => x.inventory_id === item.inventory_id)
                    ?.quantity.toString() ?? ''
                }
                max={item.available}
              />
            </div>
          ))}
        </div>
      )}
    </>
  );
};

export default ReturningInventoriesForm;
